import { useState } from "react";
import { Plus, Pencil, Trash2, Paperclip, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, Empty } from "@/components/common";
import { GstBillDialog } from "@/components/gst/GstBillDialog";
import { api, errMsg } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";

const ITC_TONE = { pending: "text-amber-700", claimed: "text-emerald-700", ineligible: "text-slate-500" };

const openPhoto = async (b) => {
  try { const r = await api.get(`/gst/bills/${b.id}/photo`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch (e) { toast.error(errMsg(e)); }
};

const ItcCell = ({ b, month, onChanged }) => {
  const [period, setPeriod] = useState(b.itc_period || month);
  const set = async (status, p = period) => {
    try { await api.put(`/gst/bills/${b.id}/itc`, { status, period: p }); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <NativeSelect className={`h-8 w-32 text-xs font-semibold ${ITC_TONE[b.itc_status]}`} value={b.itc_status} onChange={(e) => set(e.target.value)} data-testid={`bill-itc-${b.id}`}>
        <option value="pending">ITC pending</option><option value="claimed">Claimed</option><option value="ineligible">Not eligible</option>
      </NativeSelect>
      {b.itc_status === "claimed" && <Input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); set("claimed", e.target.value); }} className="h-8 w-36 text-xs" data-testid={`bill-itc-period-${b.id}`} />}
    </div>
  );
};

export const GstBills = ({ bills, month, reload }) => {
  const [edit, setEdit] = useState(null);
  const remove = async (b) => { if (!window.confirm(`Delete bill ${b.bill_no}?`)) return; await api.delete(`/gst/bills/${b.id}`); reload(); };
  return (
    <div data-testid="gst-bills">
      <div className="mb-3 flex justify-end"><Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setEdit({})} data-testid="add-bill-btn"><Plus className="mr-1 h-4 w-4" />Add purchase bill</Button></div>
      {bills.length === 0 ? <Empty icon={Receipt} title="No purchase bills this month" text="Add each grocery GST bill with its photo to claim input tax credit." testid="gst-bills-empty" /> : (
        <div className="space-y-2">
          {bills.map((b) => (
            <div key={b.id} className="grid gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-4 md:grid-cols-[1.4fr_1fr_auto_auto] md:items-center" data-testid={`bill-row-${b.id}`}>
              <div><div className="font-semibold">{b.supplier} <span className="font-mono text-xs text-muted-foreground">#{b.bill_no}</span></div>
                <div className="text-xs text-muted-foreground">{fmtDate(b.bill_date)} · {b.supplier_gstin || "No GSTIN"}{b.interstate ? " · IGST" : ""}</div></div>
              <div className="font-mono text-sm">Taxable {inr(b.taxable)} · <span className="font-semibold">GST {inr(b.tax)}</span></div>
              <ItcCell b={b} month={month} onChanged={reload} />
              <div className="flex gap-1">
                {b.photo_path ? <Button size="icon" variant="ghost" onClick={() => openPhoto(b)} data-testid={`bill-photo-${b.id}`}><Paperclip className="h-4 w-4 text-emerald-700" /></Button>
                  : <span className="self-center rounded bg-rose-50 px-1.5 text-[10px] font-semibold text-rose-600" data-testid={`bill-no-photo-${b.id}`}>No photo</span>}
                <Button size="icon" variant="ghost" onClick={() => setEdit(b)} data-testid={`bill-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(b)} data-testid={`bill-delete-${b.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <GstBillDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
};
